import type { PlanSlug, UserSubscription } from '@/api/types';
import { mockPlans } from '@/mocks/data';
import type { ApiResponse } from '@/api/transport';
import { route } from './handlers';
import { getState, setSubscription, subscriptionView } from './state';

const ok = (body: unknown, status = 200): ApiResponse => ({ status, body });
/** Тело ошибки как у бэкенда: `error` — код, остальное — данные для клиента (409 с hint/current). */
const fail = (
  status: number,
  message: string,
  error?: string,
  extra: Record<string, unknown> = {},
): ApiResponse => ({ status, body: { message, error, ...extra } });

/**
 * Биллинг в mock: подписка живёт в состоянии, чекаут завершает mock-оверлей
 * (`/__mock/checkout/complete`), вебхуков нет — /me отражает изменения сразу.
 */
const DAY = 86_400_000;

function planBySlug(slug: PlanSlug) {
  const plan = mockPlans.find((p) => p.slug === slug)!;
  return { id: plan.id, name: `Pro · ${plan.name}`, slug: plan.slug };
}

function activate(slug: PlanSlug): UserSubscription {
  const days = slug === 'yearly' ? 365 : 30;
  const subscription: UserSubscription = {
    plan: planBySlug(slug),
    expires_at: new Date(Date.now() + days * DAY).toISOString(),
    paddle_subscription_id: `sub_mock_${Date.now().toString(36)}`,
    paddle_status: 'active',
    status: 'active',
    cancelled_at: null,
    payment_method: { brand: 'Visa', last4: '4242' },
  };
  setSubscription(subscription);
  return subscription;
}

route('GET', '/subscriptions/config', () =>
  ok({
    env: 'sandbox',
    client_token: '',
    prices: { monthly: 'pri_mock_monthly', yearly: 'pri_mock_yearly' },
  }),
);

route('POST', '/subscriptions/checkout', ({ body }) => {
  const plan = body['plan'];
  if (plan !== 'monthly' && plan !== 'yearly') return fail(422, 'The plan field is invalid.');
  // Guard как у бэкенда: любая строка, которую Paddle ещё может списать (active · past_due · paused).
  const current = subscriptionView().paid;
  if (current) {
    const hint =
      current.status === 'paused'
        ? 'paused'
        : current.paddle_status === 'past_due'
          ? 'past_due'
          : current.cancelled_at
            ? 'resume'
            : current.plan?.slug === plan
              ? 'already_subscribed'
              : 'change_plan';
    return fail(409, 'You already have an active subscription.', 'subscription_exists', {
      hint,
      current: {
        paddle_subscription_id: current.paddle_subscription_id ?? null,
        plan_slug: current.plan?.slug ?? null,
        cancelled_at: current.cancelled_at ?? null,
        expires_at: current.expires_at,
      },
    });
  }
  const customData: Record<string, string> = { user_id: '1' };
  if (typeof body['correlation_id'] === 'string')
    customData['correlation_id'] = body['correlation_id'];
  return ok({
    price_id: `pri_mock_${plan}`,
    customer: { email: 'alex@studio.com' },
    custom_data: customData,
    customer_id: null,
  });
});

/** Mock-оверлей чекаута: «оплата» прошла. */
route('POST', '/__mock/checkout/complete', ({ body }) => {
  const plan = body['plan'];
  if (plan !== 'monthly' && plan !== 'yearly') return fail(422, 'plan is required');
  return ok({ ok: true, subscription: activate(plan) });
});

route('POST', '/subscriptions/resume', () => {
  const current = getState().subscription;
  if (!current || !current.cancelled_at)
    return fail(404, 'No scheduled cancellation to resume.', 'no_cancelled_subscription');
  setSubscription({ ...current, cancelled_at: null, status: 'active', paddle_status: 'active' });
  return ok({ ok: true, message: 'Subscription resumed. No charge was made.' });
});

route('POST', '/subscriptions/change-plan', ({ body }) => {
  const plan = body['plan'];
  if (plan !== 'monthly' && plan !== 'yearly') return fail(422, 'The plan field is invalid.');
  const current = getState().subscription;
  if (!current || current.cancelled_at)
    return fail(
      409,
      'No active subscription to change. Use /checkout instead.',
      'no_active_subscription',
    );
  if (current.plan?.slug === plan)
    return fail(409, 'You are already on this plan.', 'already_on_plan');
  setSubscription({ ...current, plan: planBySlug(plan) });
  return ok({ ok: true, message: 'Plan change requested. Charges applied with proration.' });
});

route('GET', '/subscriptions/portal', () => {
  const current = getState().subscription;
  if (!current?.paddle_subscription_id) return fail(404, 'No Paddle customer');
  return ok({ url: 'https://sandbox-customer-portal.paddle.com/cpl_mock' });
});

route('POST', '/subscriptions/cancel', () => {
  const current = getState().subscription;
  if (!current || current.cancelled_at) return fail(404, 'No active subscription');
  // Отмена с конца периода: статус остаётся active до expires_at, отмену помечает cancelled_at.
  setSubscription({ ...current, cancelled_at: new Date().toISOString(), status: 'active' });
  return ok({ ok: true, expires_at: current.expires_at });
});
